import { UploadStatus } from "@/shared/enums/upload-status";
import { t } from "i18next";
import React from "react";
import { useLocation } from "react-router-dom";
import {
	POSITION_CLASSES,
	ToastPosition,
	UPLOAD_STATUS_CONFIG,
} from "./constants/uploadStatusConfig";
import { useUploadProgressToast } from "./hooks/useUploadProgressToast";
import { useUploadStatusMessage } from "./hooks/useUploadStatusMessage";
import "./UploadProgressToast.scss";

export interface UploadProgressItem {
	id: string;
	fileName: string;
	status: UploadStatus;
	datasetId: string;
}

interface UploadProgressToastProps {
	uploads: UploadProgressItem[];
	onClose?: () => void;
	position?: ToastPosition;
	autoHide?: boolean;
}

const UploadProgressToast: React.FC<UploadProgressToastProps> = ({
	uploads = [],
	onClose = () => {},
	position = "bottom-right",
	autoHide = true,
}) => {
	const location = useLocation();
	const isInSecondStep = location.pathname.includes("/second-step/");

	const {
		isVisible,
		activeUploads,
		completedCount,
		errorCount,
		handleManualClose,
	} = useUploadProgressToast({ uploads, onClose, autoHide });

	// Filtrar uploads completados SOLO para visualización si estamos en second-step
	const visibleUploads = React.useMemo(() => {
		if (isInSecondStep) {
			return uploads.filter(
				(upload) => upload.status !== UploadStatus.COMPLETED,
			);
		}
		return uploads;
	}, [uploads, isInSecondStep]);

	const mainStatusMessage = useUploadStatusMessage(
		activeUploads,
		completedCount,
		errorCount,
		isInSecondStep,
	);

	if (!isVisible) return null;

	const getStatusIcon = (status: UploadStatus): string => {
		return (
			UPLOAD_STATUS_CONFIG[status]?.icon ||
			"mdi mdi-clock-outline upload-pending"
		);
	};

	const getStatusText = (status: UploadStatus): string => {
		return t(UPLOAD_STATUS_CONFIG[status]?.text || "catalogue.status.pending");
	};

	const getProgressWidth = (status: UploadStatus): string => {
		return UPLOAD_STATUS_CONFIG[status]?.progressWidth || "10%";
	};

	return (
		<div className={`upload-progress-toast ${POSITION_CLASSES[position]}`}>
			<div className="upload-toast-container">
				<div className="upload-toast-header">
					<div className="upload-toast-header-content">
						<div className="upload-toast-icon-wrapper">
							{activeUploads.length > 0 ? (
								<i className="mdi mdi-cloud-upload upload-toast-icon upload-spinning" />
							) : errorCount > 0 ? (
								<i className="mdi mdi-alert-circle upload-toast-icon upload-error" />
							) : (
								<i className="mdi mdi-check-circle upload-toast-icon upload-completed" />
							)}
						</div>
						<div className="upload-toast-content-header">
							<h4 className="upload-toast-title">{mainStatusMessage}</h4>
							{visibleUploads.length > 1 && (
								<span className="upload-toast-subtitle">
									{t("catalogue.upload-progress.total-files", {
										count: visibleUploads.length,
									})}
								</span>
							)}
						</div>
					</div>
					<button
						type="button"
						onClick={handleManualClose}
						className={`upload-toast-close-button ${errorCount > 0 ? "has-errors" : ""}`}
						title={
							errorCount > 0 ? t("catalogue.upload-close-all-hint") : t("close")
						}
					>
						<i className="mdi mdi-close" />
					</button>
				</div>

				<div className="upload-toast-content">
					{visibleUploads.slice(0, 3).map((upload) => (
						<div key={upload.id} className="upload-item">
							<div className="upload-item-header">
								<div className="upload-item-info">
									<i className={getStatusIcon(upload.status)} />
									<div className="upload-item-text">
										<span
											className="upload-item-filename"
											title={upload.fileName}
										>
											{upload.fileName}
										</span>
										<span className="upload-item-status">
											{getStatusText(upload.status)}
										</span>
									</div>
								</div>
							</div>

							{(upload.status === UploadStatus.UPLOADING ||
								upload.status === UploadStatus.ANALYZING ||
								upload.status === UploadStatus.PENDING) && (
								<div className="upload-progress-container">
									<div className="upload-progress-bar">
										<div
											className="upload-progress-fill"
											style={{ width: getProgressWidth(upload.status) }}
										/>
									</div>
								</div>
							)}
						</div>
					))}

					{visibleUploads.length > 3 && (
						<div className="upload-more-items">
							<span>
								{t("catalogue.upload-progress.more-files", {
									count: visibleUploads.length - 3,
								})}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default UploadProgressToast;
